import React, { useEffect, useRef, useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import { FaPaperclip, FaTimes } from 'react-icons/fa';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import {
  formatAttachmentFileType,
  formatFileSize,
  getMessageAttachmentValidationError,
  isMessageImageFile,
  MESSAGE_ATTACHMENT_ACCEPT,
} from '../../../util/messageAttachments';

import {
  ErrorMessage,
  FieldTextInput,
  Form,
  Heading,
  IconInquiry,
  PrimaryButton,
} from '../../../components';

import css from './InquiryForm.module.css';

/**
 * The InquiryForm component.
 * NOTE: this InquiryForm is only for booking & purchase processes
 * The default-inquiry process is handled differently
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.submitButtonWrapperClassName] - Custom class to be passed for the submit button wrapper
 * @param {boolean} [props.inProgress] - Whether the inquiry is in progress
 * @param {string} props.listingTitle - The listing title
 * @param {string} props.authorDisplayName - The author display name
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @returns {JSX.Element} inquiry form component
 */
const InquiryForm = props => {
  const intl = useIntl();
  const [selectedFile, setSelectedFile] = useState(null);
  const [attachmentError, setAttachmentError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    return () => {
      if (previewUrl && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const removeAttachment = form => {
    setSelectedFile(null);
    setAttachmentError(null);
    setPreviewUrl(null);
    form.change('attachment', null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAttachmentChange = (event, form) => {
    const nextFile = event.target.files?.[0] || null;
    const nextError = getMessageAttachmentValidationError(nextFile);
    const nextPreviewUrl =
      nextFile &&
      isMessageImageFile(nextFile) &&
      typeof URL !== 'undefined' &&
      typeof URL.createObjectURL === 'function'
        ? URL.createObjectURL(nextFile)
        : null;

    setSelectedFile(nextFile);
    setAttachmentError(nextError);
    setPreviewUrl(nextPreviewUrl);
    form.change('attachment', nextError ? null : nextFile);
  };

  const handleFormSubmit = (event, handleSubmit, form) => {
    const submitPromise = handleSubmit(event);

    if (submitPromise && typeof submitPromise.then === 'function') {
      return submitPromise.then(result => {
        removeAttachment(form);
        return result;
      });
    }

    return submitPromise;
  };

  return (
    <FinalForm
      {...props}
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          submitButtonWrapperClassName,
          formId,
          handleSubmit,
          inProgress = false,
          listingTitle,
          authorDisplayName,
          sendInquiryError,
          uploadProgress,
          values,
          invalid,
          form,
        } = fieldRenderProps;

        const messageLabel = intl.formatMessage(
          {
            id: 'InquiryForm.messageLabel',
          },
          { authorDisplayName }
        );
        const messagePlaceholder = intl.formatMessage(
          {
            id: 'InquiryForm.messagePlaceholder',
          },
          { authorDisplayName }
        );

        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = inProgress;
        const hasMessage = !!values?.message?.trim();
        const hasAttachment = !!selectedFile && !attachmentError;
        const hasUploadProgress = submitInProgress && typeof uploadProgress === 'number';
        const submitDisabled =
          submitInProgress || invalid || !!attachmentError || (!hasMessage && !hasAttachment);

        return (
          <Form
            className={classes}
            onSubmit={event => handleFormSubmit(event, handleSubmit, form)}
            enforcePagePreloadFor="OrderDetailsPage"
          >
            <IconInquiry className={css.icon} />
            <Heading as="h2" rootClassName={css.heading}>
              <FormattedMessage id="InquiryForm.heading" values={{ listingTitle }} />
            </Heading>
            <FieldTextInput
              className={css.field}
              type="textarea"
              name="message"
              id={formId ? `${formId}.message` : 'message'}
              label={messageLabel}
              placeholder={messagePlaceholder}
            />
            {selectedFile ? (
              <div
                className={classNames(css.attachmentPreview, {
                  [css.attachmentPreviewInvalid]: !!attachmentError,
                })}
              >
                <div className={css.attachmentInfo}>
                  {previewUrl ? (
                    <img className={css.attachmentThumbnail} src={previewUrl} alt="" />
                  ) : (
                    <FaPaperclip className={css.attachmentPreviewIcon} aria-hidden="true" />
                  )}
                  <div className={css.attachmentText}>
                    <span className={css.attachmentName}>{selectedFile.name}</span>
                    <span className={css.attachmentMeta}>
                      {formatAttachmentFileType(selectedFile)} -{' '}
                      {formatFileSize(selectedFile.size)}
                    </span>
                  </div>
                </div>
                <button
                  className={css.removeAttachmentButton}
                  type="button"
                  onClick={() => removeAttachment(form)}
                  disabled={submitInProgress}
                  aria-label="Remove attachment"
                >
                  <FaTimes aria-hidden="true" />
                </button>
                {hasUploadProgress ? (
                  <div className={css.uploadProgress}>
                    <div
                      className={css.uploadProgressBar}
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
            <div className={css.attachmentActions}>
              <label
                className={classNames(css.attachmentButton, {
                  [css.attachmentButtonDisabled]: submitInProgress,
                })}
              >
                <FaPaperclip aria-hidden="true" />
                <span>Attach</span>
                <input
                  ref={fileInputRef}
                  className={css.fileInput}
                  type="file"
                  accept={MESSAGE_ATTACHMENT_ACCEPT}
                  disabled={submitInProgress}
                  onChange={event => handleAttachmentChange(event, form)}
                />
              </label>
            </div>
            <div className={submitButtonWrapperClassName}>
              <ErrorMessage error={sendInquiryError} />
              {attachmentError ? <p className={css.error}>{attachmentError}</p> : null}
              <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
                <FormattedMessage id="InquiryForm.submitButtonText" />
              </PrimaryButton>
            </div>
          </Form>
        );
      }}
    />
  );
};

export default InquiryForm;
